import { tokenize, unique } from './tokenizer';
import { ComponentMapEntry, BugfixInput, TargetElementHint } from './types';

export function scoreEntry(entry: ComponentMapEntry, tokens: string[]): number {
  const haystack = new Set(entry.searchTokens || []);
  return tokens.reduce((score, token) => score + (haystack.has(token) ? 1 : 0), 0);
}

export function scoreSelectorTokens(selector: string, tokens: string[]): number {
  const selectorTokens = tokenize(selector);
  return selectorTokens.reduce((score, token) => score + (tokens.includes(token) ? 1 : 0), 0);
}

export function isEntryInTargetApp(entry: ComponentMapEntry, appName: string): boolean {
  const sourcePath = String(entry?.sourcePath || '');
  if (!sourcePath.startsWith('apps/')) {
    return true;
  }

  return sourcePath.startsWith(`apps/${appName}/`);
}

export function getTargetElementSearchText(input: BugfixInput): string {
  const target = input?.runtimeContext?.target_element ||
    input?.reproductionPayload?.target_element ||
    input?.reproductionPayload?.runtime_context?.target_element;
  if (!target || typeof target !== 'object' || Array.isArray(target)) {
    return '';
  }

  return [
    target.selector,
    ...(Array.isArray(target.selectorCandidates) ? target.selectorCandidates : []),
    ...(Array.isArray(target.componentChain) ? target.componentChain : []),
    ...(Array.isArray(target.relatedOwners)
      ? target.relatedOwners.flatMap(owner => [
          owner.kind,
          owner.selector,
          owner.tagName,
          ...(Array.isArray(owner.classes) ? owner.classes : [])
        ])
      : []),
    target.structuralOwner?.selector,
    target.structuralOwner?.overlayScope,
    target.structuralOwner?.iconSelector,
    target.structuralOwner?.inputSelector,
    target.structuralOwner?.tagName,
    ...(Array.isArray(target.structuralOwner?.classes) ? target.structuralOwner.classes : []),
    target.surface,
    target.role,
    target.tagName,
    target.label,
    target.text
  ]
    .filter(Boolean)
    .join('\n')
    .toLowerCase();
}

export function isHomePageBug(input: BugfixInput): boolean {
  const route = String(input.path || '').trim().toLowerCase();
  return route === '/home' || route === '/' || String(input.scope || '').trim().toLowerCase() === 'home page';
}

export function isHomeMoviesCarouselBug(input: BugfixInput): boolean {
  if (!isHomePageBug(input)) {
    return false;
  }

  const targetText = getTargetElementSearchText(input);
  const text = `${input.bugContext || ''}\n${input.expectedResult || ''}\n${(input.stepsToReproduce || []).join('\n')}`.toLowerCase();
  return /ush-movies-carousel|carousel__title|carousel__view-all/.test(targetText) ||
    /\bmovie\s*slider\b|\bmovies?\s*carousel\b|\bcarousel\s+title\b/.test(text);
}

export function isHomeChannelCarouselBug(input: BugfixInput): boolean {
  if (!isHomePageBug(input)) {
    return false;
  }

  const targetText = getTargetElementSearchText(input);
  const text = `${input.bugContext || ''}\n${input.expectedResult || ''}\n${(input.stepsToReproduce || []).join('\n')}`.toLowerCase();
  return /ush-feature-channel-video|ush-feature-channel-carousel|alti-channel-carousel/.test(targetText) ||
    /\bchannel\s*video\b|\bchannel\s*carousel\b|\bhero\b|\btrailer\b|\bbackground\b/.test(text);
}

export function isRowWrapLayoutBug(input: BugfixInput): boolean {
  const text = `${input.bugContext || ''}\n${input.expectedResult || ''}\n${(input.stepsToReproduce || []).join('\n')}`.toLowerCase();
  const rowSignals = ['same row', 'single row', 'one row', 'one line', 'same line', 'two rows', 'wrap', 'wrapped'];
  const layoutSignals = ['width', 'nowrap', 'row', 'line', 'horizontal', 'stack', 'stacked', 'min-content', 'max-content'];

  return rowSignals.some(signal => text.includes(signal)) && layoutSignals.some(signal => text.includes(signal));
}

export function scoreAppReachability(entry: ComponentMapEntry, appName: string): number {
  const sourcePath = String(entry?.sourcePath || '');
  if (!sourcePath.startsWith('libs/')) return 0;

  const usedBy = entry.usedBy || [];
  if (usedBy.length === 0) return 0;

  const reachableBy = entry.reachableByApps || [];
  if (reachableBy.length === 0) return 0;
  if (!reachableBy.includes(appName)) return -50;
  return 0;
}

export function applyRouteSpecificEntryScore(entry: ComponentMapEntry, input: BugfixInput): number {
  const sourcePath = String(entry?.sourcePath || '');
  const selector = String(entry?.selector || '');
  let score = 0;

  if (isHomePageBug(input)) {
    if (isHomeMoviesCarouselBug(input) && /custom-page\/feature-movies-carousel/.test(sourcePath)) {
      score += 70;
    }

    if (isHomeMoviesCarouselBug(input) && /ush-movies-carousel/.test(selector)) {
      score += 70;
    }

    if (isHomeChannelCarouselBug(input) && (
      /custom-page\/feature-channel-carousel|custom-page\/feature-channel-video|\/pages\/home\/|\/components\/alti-channel-video\//.test(sourcePath) ||
      /ush-feature-channel-carousel|ush-feature-channel-video|alti-channel-carousel/.test(selector)
    )) {
      score += 40;
    }

    if (isHomeMoviesCarouselBug(input) && /custom-page\/feature-channel-carousel|custom-page\/feature-channel-video/.test(sourcePath)) {
      score -= 30;
    }

    if (/libs\/movie-detail\//.test(sourcePath) || /feature-movie-awareness-page|feature-movie-conversion-page/.test(selector)) {
      score -= 50;
    }
  }

  return score;
}

export function extractNestedComponentSelectors(template: string): string[] {
  return unique(
    [...String(template || '').matchAll(/<([a-z][a-z0-9-]*-[a-z0-9-]+)\b/g)]
      .map(match => String(match[1] || '').trim())
      .filter(Boolean)
  );
}

export function scoreInteractiveTargetMatch(entry: ComponentMapEntry, input: BugfixInput): number {
  const target = input?.runtimeContext?.target_element ||
    input?.reproductionPayload?.target_element ||
    input?.reproductionPayload?.runtime_context?.target_element;
  if (!target || typeof target !== 'object' || Array.isArray(target)) {
    return 0;
  }

  const interactiveSelectors = entry.selectors?.interactive || [];
  if (interactiveSelectors.length === 0) {
    return 0;
  }

  let score = 0;
  const targetSelectors = [
    target.selector,
    ...(Array.isArray(target.selectorCandidates) ? target.selectorCandidates : [])
  ].filter(Boolean).map(s => String(s).toLowerCase());

  const targetClasses = targetSelectors
    .flatMap(s => [...s.matchAll(/\.([a-zA-Z0-9_-]+)/g)])
    .map(m => m[1].toLowerCase());

  for (const interactive of interactiveSelectors) {
    const lowered = interactive.toLowerCase();
    if (targetSelectors.some(ts => ts === lowered || ts.includes(lowered) || lowered.includes(ts))) {
      score += 10;
    }
    if (targetClasses.some(tc => lowered.includes('.' + tc) || lowered.includes(tc))) {
      score += 5;
    }
  }

  return score;
}

export function scoreComponentChainMatch(entry: ComponentMapEntry, input: BugfixInput): number {
  const target = input?.runtimeContext?.target_element ||
    input?.reproductionPayload?.target_element ||
    input?.reproductionPayload?.runtime_context?.target_element;
  if (!target || typeof target !== 'object' || Array.isArray(target)) {
    return 0;
  }

  const componentChain = Array.isArray(target.componentChain) ? target.componentChain : [];
  if (componentChain.length === 0) {
    return 0;
  }

  let score = 0;
  const entrySymbol = (entry.symbolName || '').toLowerCase();
  const entrySelector = (entry.selector || '').toLowerCase();
  const entryPath = (entry.sourcePath || '').toLowerCase();

  for (const chainItem of componentChain) {
    const lowered = chainItem.toLowerCase();
    if (entrySymbol === lowered || entrySymbol.includes(lowered) || lowered.includes(entrySymbol)) {
      score += 1000;
    }
    if (entrySelector && (entrySelector.includes(lowered) || lowered.includes(entrySelector))) {
      score += 500;
    }
    if (entryPath.includes(lowered)) {
      score += 100;
    }
  }

  return score;
}
